import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { useAnnounce } from "../app/announcer";
import { useRouter } from "../app/router";
import { selectDirection, useStudio } from "../app/store";
import { CorrectionBench } from "./CorrectionBench";
import { DirectionCardView } from "./DirectionCard";
import { ExportWrap } from "./ExportWrap";
import { PreviewLab } from "./PreviewLab";
import { Processing } from "./Processing";
import { QuestionFlow, type BriefPageProgress } from "./QuestionFlow";
import { SourceTable } from "./SourceTable";

const progressFor = (guided: boolean, step: string, page: BriefPageProgress | null) => {
  const total = guided ? 5 : 2;
  if (["direction", "lab", "bench", "export"].includes(step)) return { current: total, total, label: "Your palette" };
  if (step === "brief") return { current: Math.min(total - 1, (page?.page ?? 0) + 2), total, label: page?.title ?? "Fit to the deck" };
  if (step === "processing") return { current: total - 1, total, label: "Building the color worlds" };
  return { current: 1, total, label: "Choose an image" };
};

export const Studio = () => {
  const { state, dispatch } = useStudio();
  const { navigate } = useRouter();
  const announce = useAnnounce();
  const mainRef = useRef<HTMLDivElement>(null);
  const [briefPage, setBriefPage] = useState<BriefPageProgress | null>(null);
  const direction = selectDirection(state);
  const guided = state.brief.colourPath === "guided" || state.colourPath === "guided";

  useEffect(() => {
    if (state.entry !== "colour") navigate("/");
  }, [state.entry, navigate]);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    const frame = window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    const heading = mainRef.current?.querySelector<HTMLElement>("h2");
    if (heading) {
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
    }
    return () => window.cancelAnimationFrame(frame);
  }, [state.step]);

  useEffect(() => {
    if (state.step !== "brief") setBriefPage(null);
  }, [state.step]);

  if (state.entry !== "colour") return null;

  const progress = progressFor(guided, state.step, briefPage);
  const remaining = progress.total - progress.current;
  const remainingLabel = remaining === 0 ? "Done" : remaining === 1 ? "One step left" : `${remaining} steps left`;

  const choosePath = (path: "quick" | "guided") => {
    dispatch({ type: "setBrief", brief: { ...state.brief, route: "colour", colourPath: path } });
    if (path === "guided") {
      dispatch({ type: "setStep", step: "brief" });
      announce("Three useful pages, then your palette.");
    } else {
      dispatch({ type: "setStep", step: "processing" });
      announce("Building your palette from the whole image.");
    }
  };

  return (
    <div ref={mainRef} className="studio-shell">
      <header className="journey-header">
        <div className="journey-meta"><span className="journey-route">Color Studio</span><span className="journey-count">Step {progress.current} of {progress.total}</span></div>
        <div className="journey-label-row"><strong>{progress.label}</strong><span>{remainingLabel}</span></div>
        <div className="journey-track" aria-label={`Step ${progress.current} of ${progress.total}`} role="progressbar" aria-valuemin={1} aria-valuemax={progress.total} aria-valuenow={progress.current}>
          {Array.from({ length: progress.total }, (_, index) => <span key={index} data-state={index + 1 < progress.current ? "done" : index + 1 === progress.current ? "current" : "upcoming"} />)}
        </div>
      </header>

      <div className="studio-page">
        {state.step === "source" ? <SourceTable onContinue={() => choosePath(state.colourPath ?? "quick")} onChooseColourPath={choosePath} onBack={() => navigate("/")} /> : null}
        {state.step === "brief" ? <QuestionFlow onDone={() => { dispatch({ type: "setStep", step: "processing" }); announce("Building your palette."); }} onProgress={setBriefPage} onBack={() => dispatch({ type: "setStep", step: "source" })} /> : null}
        {state.step === "processing" ? <Processing onCancelled={() => dispatch({ type: "setStep", step: "source" })} /> : null}
        {state.step === "direction" && direction ? <DirectionCardView direction={direction} onOpenLab={() => dispatch({ type: "setStep", step: "lab" })} onOpenBench={() => dispatch({ type: "setStep", step: "bench" })} onOpenExport={() => dispatch({ type: "setStep", step: "export" })} /> : null}
        {state.step === "lab" ? <><button type="button" className="back-to-result" onClick={() => dispatch({ type: "setStep", step: "direction" })}>← Back to my result</button><PreviewLab /></> : null}
        {state.step === "bench" ? <><button type="button" className="back-to-result" onClick={() => dispatch({ type: "setStep", step: "direction" })}>← Back to my result</button><CorrectionBench /></> : null}
        {state.step === "export" ? <><button type="button" className="back-to-result" onClick={() => dispatch({ type: "setStep", step: "direction" })}>← Back to my result</button><ExportWrap /></> : null}
      </div>
    </div>
  );
};
